import React, { useContext, useState, useEffect, useRef } from 'react';
import { Avatar, Box, Button, Flex, Icon, IconButton, Image, Text, VStack, useToast } from '@chakra-ui/react';
import { Link as RouterLink, useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import { FiAlertTriangle, FiCheck, FiChevronLeft, FiChevronRight, FiClock, FiMessageSquare, FiPackage, FiSmartphone, FiVideo } from 'react-icons/fi';
import CreativeBrief from '../components/creative-brief/CreativeBrief';
import { collabApi } from '../../../services/api/collab';
import { InfluencerCampaignDataContext, InfluencerCampaignDataLoadingContext } from '../../../contexts/InfluencerCampaignDataContext';
import DeclineReasonModal from '../components/counter-flow/DeclineReasonModal';
import CounterReasonModal from '../components/counter-flow/CounterReasonModal';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { getInfluencerPrimaryHandle } from '../../../utils/influencerUtils';
import { Influencer } from '@foodfluence/shared';
import ShippingDetailsModal from '../components/ShippingDetailsModal';
import { productSelectionDestination } from '../utils/productSelection';
import { acceptCollabNotification } from 'services/flaskService';
import { CollabDataContext } from 'contexts/collabDataContext';
import { canCounter, MAX_COUNTERS } from '../utils/counterUtils';
import { isRetailCampaignType, isShippingCampaignType, isBrandManagedCampaignType } from 'utils/collabUtils';
import { isClientHandledCredit } from '../utils/collabUtils';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../../firebase';
import { shouldShowVerificationOverlay } from '../../../utils/authUtils';
import PhoneVerificationModal from '../../../components/PhoneVerificationModal';
import ExtensionRequestModal from '../components/ExtensionRequestModal';
import { getPostByNoSlotsMessage } from '../utils/postByNoSlots';
import { getBrowserTimezone } from 'utils/timezoneUtils';
import { fetchSchedulingCapacityStatus } from 'hooks/useSchedulingAvailability';
import { needsExtensionRequest, isCampaignFull, isUnconfirmedCollab, collabCanBeExpired, isCampaignEnded, hasRecentExtensionRequest, hasRecentWaitlistRequest, requestExtensionReplacesAccept } from '../utils/collabUtils';
import { ClientDataContext } from 'contexts/ClientDataContext';
import { getClientIdFromCollabRequest } from 'utils/collabUtils';
import { formatFirebaseDate, toDate } from 'utils/dateUtils';
import { getEffectiveEndDate } from 'utils/collabUtils';
import { collabProductLabel } from '../utils/collabProductLabel';
import PostByConfirm, { usePostByConfirm, rememberGoLiveConfirmation, isCampaignFullError } from '../components/PostByConfirm';
import { formatUgcDeliverablesLine, formatUgcPostingNote, summarizeUgcDeliverables } from 'utils/contentUtils';
import { getTurnaroundText, getTurnaroundTitle } from '../utils/creativeBriefUtils';
import { acceptLabelFor, formatExpiresIn } from '../utils/reviewInvite';
import { CAMPAIGN_TYPE_CHIP, getDeliverableLines, showsCredit } from '../../explore/utils';
import { openCrispChat } from '../../../components/FrostedSupportButton';
import { ff } from '../../../theme';

const HERO_BUTTON = {
  w: '44px',
  h: '44px',
  minW: '44px',
  borderRadius: 'full',
  bg: 'white',
  color: ff.ink,
  _hover: { bg: ff.grey },
} as const;

/** Secondary dock buttons (Counter, Decline): board ReviewInvite row 2. */
const DOCK_BUTTON = {
  flex: 1,
  minW: 0,
  h: '44px',
  borderRadius: '14px',
  bg: '#2A2A2C',
  color: 'white',
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: 'normal',
  _hover: { bg: '#2A2A2C' },
  _active: { bg: '#3A3A3C' },
} as const;

const Fact: React.FC<{ icon: React.ElementType; children: React.ReactNode }> = ({ icon, children }) => (
  <Flex as="li" align="center" gap="10px" fontSize="15px" lineHeight="normal">
    <Icon as={icon} boxSize="18px" flex="none" aria-hidden />
    <Text as="span">{children}</Text>
  </Flex>
);

type Banner = { title: string; sub: string; ok?: boolean };

const ReviewCollabPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: collabId } = useParams<{ id: string }>();
  const [user] = useAuthState(auth);
  
  const toast = useToast();

  const campaignData = useContext(InfluencerCampaignDataContext);
  const campaignLoading = useContext(InfluencerCampaignDataLoadingContext);
  
  const { influencerData } = useContext(InfluencerDataContext) as { influencerData: Influencer | null };

  const collabData = useContext(CollabDataContext);
  const clients = useContext(ClientDataContext);

  const [isDeclineReasonModalOpen, setIsDeclineReasonModalOpen] = useState(false);
  const [isCounterReasonModalOpen, setIsCounterReasonModalOpen] = useState(false);  
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isExtensionRequestModalOpen, setIsExtensionRequestModalOpen] = useState(false);
  const [remainingSpots, setRemainingSpots] = useState<number | null>(null);
  const [schedulingFullyBooked, setSchedulingFullyBooked] = useState(false);
  const [isSpotsLoading, setIsSpotsLoading] = useState(false);
  const [isSchedulingLoading, setIsSchedulingLoading] = useState(false);

  /** Prevents duplicate record-viewed-time / Flask schedule calls (e.g. React Strict Mode double effects). */
  const recordViewTimeInitiatedForCollabRef = useRef<string | null>(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Re-render each minute so the "Expires in" pill counts down.
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick(n => n + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  // Initialize Crisp for this page
  useEffect(() => {
    if (influencerData && typeof window.$crisp !== 'undefined') {
      window.$crisp.push(['set', 'user:email', [influencerData.email_address]]);
    }
  }, [influencerData]);

  const collab = collabData.find(c => c.id === collabId);
  
  const campaign = collabId ? campaignData[collabId] : undefined;
  const client = collab && campaign ? clients[getClientIdFromCollabRequest(collab, campaign)] : undefined;

  // Go-live campaigns: Accept stays disabled until the creator ticks "I can post on …".
  const postBy = usePostByConfirm(campaign, collab);
  const postByCheckboxRef = useRef<HTMLInputElement>(null);
  /** The inbox Accept shortcut is waiting on the go-live checkbox before it accepts. */
  const autoAcceptAwaitingConfirmRef = useRef(false);

  // Record when the influencer first views this collab
  useEffect(() => {
    if (!collabId || collab?.collabOfferFirstReviewedTime) {
      return;
    }
    if (recordViewTimeInitiatedForCollabRef.current === collabId) {
      return;
    }
    recordViewTimeInitiatedForCollabRef.current = collabId;

    const recordViewTime = async () => {
      await collabApi.recordCollabViewedTime({ collabId: collabId }, influencerData?.id);
    };
    
    // Best-effort: a failed view stamp must not surface as an unhandled rejection.
    recordViewTime().catch(() => {});
    // influencerData?.id stays only to satisfy exhaustive-deps; the ref guard stops a second send.
  }, [collabId, collab?.collabOfferFirstReviewedTime, influencerData?.id]);

  // Fetch remaining spots and scheduling capacity once campaign data is ready
  useEffect(() => {
    if (!campaign?.id || !collab?.id || campaignLoading) {
      return;
    }

    const fetchRemainingSpots = async () => {
      if (campaign.maxCollabs !== null && collab.influencerCampaignID && isUnconfirmedCollab(collab, campaign)) {
        setIsSpotsLoading(true);
        try {
          const res = await collabApi.getAvailableSpots(collab.influencerCampaignID, collab.id);
          setRemainingSpots(res.availableSpots);
        } catch (error) {
          console.error('Error fetching remaining spots:', error);
        } finally {
          setIsSpotsLoading(false);
        }
      }
    };

    const fetchSchedulingStatus = async () => {
      setIsSchedulingLoading(true);
      try {
        const status = await fetchSchedulingCapacityStatus(campaign, collab, collab.selectedLocation);
        setSchedulingFullyBooked(status.schedulingFullyBooked);
      } catch (error) {
        console.error('Error fetching scheduling capacity status:', error);
      } finally {
        setIsSchedulingLoading(false);
      }
    };

    fetchRemainingSpots();
    fetchSchedulingStatus();

    if (collab?.contentRequirements && Object.keys(collab.contentRequirements).length > 0 && collab?.influencerCampaignID || campaign?.contentRequirements && Object.keys(campaign.contentRequirements).length > 0 && collab?.influencerCampaignID) {
      collabApi.downloadInspirationLinks(collab.influencerCampaignID).catch(() => {});
    }
  }, [campaign?.id, collab?.id, collab?.selectedLocation, campaignLoading]);

  // Accept tapped on the offer card in the chat: run this page's accept flow once the data is in.
  // The flag is read once and cleared from history on mount, so leaving before the data loads and
  // coming Back (or refreshing) can't accept without a fresh tap.
  const autoAcceptRef = useRef((location.state as { autoAccept?: boolean } | null)?.autoAccept === true);
  // Set when the card offered a waitlist / extension request rather than Accept: if the campaign
  // has room again by now, stop and let the creator accept themselves.
  const expectRequestRef = useRef((location.state as { expect?: string } | null)?.expect === 'request');
  useEffect(() => {
    if (autoAcceptRef.current) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!autoAcceptRef.current || !user || !collab || !campaign || campaignLoading || !influencerData) return;
    autoAcceptRef.current = false;
    const expectRequest = expectRequestRef.current;
    expectRequestRef.current = false;
    if (collab.status !== 'pendingInfluencerApproval') {
      toast({ title: 'This invite is no longer open', status: 'info', duration: 4000, isClosable: true });
      return;
    }
    // Phone verification gates the Accept button on this page; the chat must not skip it.
    if (shouldShowVerificationOverlay(user, influencerData)) {
      toast({
        title: 'Verify your phone to accept',
        description: 'Add your phone number, then tap Accept.',
        status: 'info', duration: 5000, isClosable: true,
      });
      return;
    }
    // The shortcut must not skip the go-live confirmation: stop at the checkbox, accept once ticked.
    if (postBy.blockedReason) {
      if (postBy.goLive && !postBy.passed && !postBy.unreachable) {
        autoAcceptAwaitingConfirmRef.current = true;
        expectRequestRef.current = expectRequest; // carried to the deferred accept below
        focusPostByCheckbox();
        toast({ title: 'Confirm the go-live dates to accept', status: 'info', duration: 5000, isClosable: true });
      } else {
        toast({ title: postBy.blockedReason, status: 'error', duration: 5000, isClosable: true });
      }
      return;
    }
    handleAccept(expectRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, collab, campaign, campaignLoading, influencerData]);

  useEffect(() => {
    if (!autoAcceptAwaitingConfirmRef.current || !postBy.confirmed) return;
    autoAcceptAwaitingConfirmRef.current = false;
    const expectRequest = expectRequestRef.current;
    expectRequestRef.current = false;
    handleAccept(expectRequest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postBy.confirmed]);

  function focusPostByCheckbox() {
    // After paint: the checkbox sits inside the brief, which renders once data is in.
    requestAnimationFrame(() => {
      postByCheckboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      postByCheckboxRef.current?.focus({ preventScroll: true });
    });
  }

  // If no collabId in URL params, redirect to collabs page
  if (!collabId) {
    console.error('No collabId found in URL params');
    navigate('/collabs');
    return null;
  }
  
  const handleAccept = async (expectRequest = false) => {
    if(collab.status != 'pendingInfluencerApproval') {
      toast({
        title: "Collab already accepted",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (isAccepting) return; // Prevent double clicks
    if (postBy.blockedReason) {
      focusPostByCheckbox();
      return;
    }
    // Carried to whichever accept step follows (scheduling, retail, shipping, product selection):
    // in navigation state, and in sessionStorage for a reload (it can be blocked).
    rememberGoLiveConfirmation(collab.id, postBy.goLiveConfirmed);
    const { goLiveConfirmed } = postBy;
    
    try {
      setIsAccepting(true); // Disable button
      
      if (!collab || !campaign) {
        toast({
          title: "Error",
          description: "Required data is missing. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Fresh checks before accept — same pattern as CollabComponent
      let latestSpots = remainingSpots;
      if (campaign.maxCollabs !== null && collab.influencerCampaignID) {
        try {
          const res = await collabApi.getAvailableSpots(collab.influencerCampaignID, collab.id);
          latestSpots = res.availableSpots;
          setRemainingSpots(res.availableSpots);
        } catch (error) {
          console.error('Error fetching remaining spots:', error);
        }
      }

      let latestSchedulingFullyBooked = schedulingFullyBooked;
      try {
        const schedulingStatus = await fetchSchedulingCapacityStatus(
          campaign,
          collab,
          collab.selectedLocation
        );
        latestSchedulingFullyBooked = schedulingStatus.schedulingFullyBooked;
        setSchedulingFullyBooked(schedulingStatus.schedulingFullyBooked);
      } catch (error) {
        console.error('Error fetching scheduling capacity status:', error);
      }

      const campaignFull = isCampaignFull(campaign, latestSpots, collab);
      const needsExtension = needsExtensionRequest(campaign, collab, latestSchedulingFullyBooked);

      if (needsExtension || campaignFull) {
        setIsExtensionRequestModalOpen(true);
        return;
      }
      if (expectRequest) {
        toast({ title: 'A spot just opened — tap Accept to join', status: 'info', duration: 5000, isClosable: true });
        return;
      }

      // A campaign whose groups offer a real choice sends the creator through the selection
      // wizard first; the choice is then carried into whichever accept step follows. Campaigns
      // with a single fixed offer skip it entirely and behave exactly as they did before.
      const destination = productSelectionDestination(campaign);
      if (destination) {
        navigate('/collabs/select-products', {
          state: {
            collabId: collab.id,
            destination,
            goLiveConfirmed,
          },
        });
        return;
      }

      if (isShippingCampaignType(campaign?.campaignType)) {
        setIsAddressModalOpen(true);
      } else if (isRetailCampaignType(campaign?.campaignType)) {
        navigate(`/collabs/schedule-retail`, {state: { collabId: collab.id, goLiveConfirmed }, replace: true});
      } else if (isBrandManagedCampaignType(campaign?.campaignType)) {
        await collabApi.acceptBrandManagedCollab(collab.id, undefined, goLiveConfirmed);
        acceptCollabNotification(collab.id);
        toast({
          title: "Collab accepted",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        navigate(`/collabs/collab-details/${collab.id}`, { replace: true });
      } else {
        navigate(`/collabs/schedule-collab`, {state: { collabId: collab.id, campaign, influencer: influencerData, goLiveConfirmed }});
      }
    } catch (error) {
      console.error('Error accepting collab:', error);
      if (isCampaignFullError(error)) setRemainingSpots(0); // the server's count wins: show the full state
      if (postBy.handleError(error)) return;
      toast({
        title: "Error accepting collab",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsAccepting(false); // Re-enable button
    }
  };

  const handleReject = () => {
    setIsDeclineReasonModalOpen(true);  // Opens the modal immediately
  };

  const handleCounter = () => {
    setIsCounterReasonModalOpen(true);
  };

  // Pre-collab messaging: open the thread without creating it. The first send creates it
  // server-side (no welcome — that waits for acceptance), so no ensureConversationExists here.
  const handleMessageBrand = () => {
    if (!collab?.id) return;
    navigate(`/inbox/messages/${collab.id}`, { state: { conversationId: collab.id, campaign, collab, client } });
  };

  const handleModalSubmit = async (reason: string) => {
    try {
      if (!collab.id) throw new Error("Collab ID is undefined");
      await collabApi.declineCollab({ collabId: collab.id, reason });
      toast({
        title: "Collab rejected",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate('/collabs');
    } catch (error) {
      console.error('Error rejecting collab:', error);
      toast({
        title: "Error rejecting collab",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeclineReasonModalOpen(false);
    }
  };

  if (!collab || !campaign) {
    return <Box>Loading...</Box>;
  }

  // This page is only for offers awaiting the creator's decision. Once accepted/scheduled (or
  // otherwise moved on), the Accept/Counter/Decline actions no longer apply — send them to the
  // collab details view. Guards every entry point (wizard "View", back button, deep link).
  if (collab.status !== 'pendingInfluencerApproval') {
    return <Navigate to={`/collabs/collab-details/${collabId}`} replace />;
  }

  const needsVerification = shouldShowVerificationOverlay(user, influencerData);
  const brandName = client?.accountName ?? 'the brand';
  const slotsFullBeforeEndDate = schedulingFullyBooked && !isCampaignEnded(campaign, collab);
  const showExtensionNeeded = needsExtensionRequest(campaign, collab, schedulingFullyBooked);
  const showRequestExtension = requestExtensionReplacesAccept(campaign, collab, schedulingFullyBooked, postBy.passed);
  const postByNoSlotsMessage = slotsFullBeforeEndDate ? getPostByNoSlotsMessage(collab, campaign, true, getBrowserTimezone()) : null;
  const extensionRequested = hasRecentExtensionRequest(collab);
  const campaignFull = isCampaignFull(campaign, remainingSpots, collab);
  const waitlistRequested = hasRecentWaitlistRequest(collab);
  const counterAllowed = canCounter(campaign, collab);
  const countersUsed = collab.counters?.length ?? 0;

  const ugcSummary = summarizeUgcDeliverables(collab, campaign);
  const deliverablesLine = formatUgcDeliverablesLine(ugcSummary) || getDeliverableLines(campaign, collab.deliverableCounts).join(' · ');
  const postingNote = formatUgcPostingNote(ugcSummary);
  const socialTag = getDeliverableLines(null, collab.deliverableCounts).map(line => line.replace(/^1x /, '')).join(' + ');
  const typeTag = campaign.creatorType === 'ugc' ? 'UGC' : socialTag;
  const clientIdForLink = getClientIdFromCollabRequest(collab, campaign);
  const kind = campaign.campaignType ? CAMPAIGN_TYPE_CHIP[campaign.campaignType] : null;
  const expiresAt = collabCanBeExpired(collab);
  const turnaround = getTurnaroundText(campaign, collab);

  const bannerImage = campaign?.collabImage || client?.bannerPicture;

  // Money row: cash, then credit (in-store only, or a value set anyway), then product.
  const productValue = Math.round(campaign?.productValue ?? 0);
  const productLabel = collabProductLabel(collab, campaign);
  const withCredit = showsCredit(campaign.campaignType, collab.credit);
  const money: { label: string; value: number }[] = [
    { label: 'Cash', value: collab.cash ?? 0 },
    ...(withCredit ? [{ label: 'Credit', value: collab.credit ?? 0 }] : []),
    ...(productValue > 0 ? [{ label: 'Product', value: productValue }] : []),
  ];
  const brandHandlesCredit = (collab.credit ?? 0) > 0 && isClientHandledCredit(collab, campaign);

  // A brand counter-back: their note and the creator's last counter, for context.
  const brandCountered = (collab.clientCounters?.length ?? 0) > 0;
  const lastCounter = collab.counters?.[collab.counters.length - 1];
  const lastCounterDate = toDate(lastCounter?.timestamp);
  const yourCounterLine = brandCountered && lastCounter && lastCounter.proposedInfluencerCash != null
    ? `Your counter${lastCounterDate ? ` (${lastCounterDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` : ''}: $${lastCounter.proposedInfluencerCash} cash${withCredit ? ` + $${lastCounter.proposedInfluencerCredit ?? 0} credit` : ''}`
    : null;

  const banners: Banner[] = [];
  if (campaignFull) {
    banners.push({ title: 'Campaign full', sub: 'This campaign has reached its maximum capacity. You can request to be added to the waitlist.' });
  }
  if (showExtensionNeeded) {
    banners.push(
      extensionRequested
        ? { ok: true, title: 'Extension requested', sub: 'Your extension request has been sent. The brand will review and get back to you soon.' }
        : slotsFullBeforeEndDate
          ? {
              title: postByNoSlotsMessage ? 'No visit times left' : 'All time slots booked',
              sub: postByNoSlotsMessage ?? 'All available time slots are currently booked. Request an extension so the brand can add more availability.',
            }
          : { title: 'End date passed', sub: "This campaign's end date has passed. You can request an extension to continue." }
    );
  }

  const acceptDisabled = !!postBy.blockedReason;
  const PRIMARY = {
    w: '100%',
    h: '56px',
    borderRadius: '18px',
    bg: ff.lime,
    color: ff.ink,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 'normal',
    _hover: { bg: ff.lime },
    _active: { bg: ff.lime },
    _disabled: { bg: '#2A2A2C', color: '#CFCFD2', opacity: 1, cursor: 'not-allowed' },
  } as const;

  return (
    <Box minH="100svh" bg="white" fontFamily="display" color={ff.ink} pb={postBy.required || postBy.blockedReason ? '320px' : '200px'}>
      {/* Hero */}
      <Box position="relative" h="300px" bg={ff.grey}>
        {bannerImage && <Image src={bannerImage} alt="" w="100%" h="100%" objectFit="cover" />}
        <IconButton
          aria-label="Back"
          icon={<FiChevronLeft size={20} strokeWidth={2.2} />}
          onClick={() => navigate('/collabs')}
          position="absolute"
          top="16px"
          left="16px"
          {...HERO_BUTTON}
        />
        <IconButton
          aria-label="Help"
          icon={<Text as="span" fontSize="18px" fontWeight={600} lineHeight="normal">?</Text>}
          onClick={openCrispChat}
          position="absolute"
          top="16px"
          right="16px"
          {...HERO_BUTTON}
        />
        <Flex position="absolute" left="16px" bottom="16px" h="28px" px="10px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" fontSize="11px" fontWeight={600} letterSpacing="0.1em" lineHeight="normal">
          {"YOU'RE INVITED"}
        </Flex>
      </Box>

      <VStack px="20px" pt="22px" spacing="24px" align="stretch">
        {/* Title block */}
        <VStack align="stretch" spacing="10px">
          <Flex
            as={RouterLink}
            to={`/explore/client/${client?.id ?? clientIdForLink}`}
            alignSelf="flex-start"
            align="center"
            gap="6px"
            minH="32px"
            color={ff.ink}
            fontSize="13px"
            fontWeight={600}
            letterSpacing="0.08em"
            textTransform="uppercase"
            lineHeight="normal"
            _hover={{ textDecoration: 'none' }}
          >
            {client?.accountName ?? ''}
            <Icon as={FiChevronRight} boxSize="14px" strokeWidth={2.4} aria-hidden />
            {kind && <Text as="span" color={ff.text2} fontWeight={600}>· {kind}</Text>}
          </Flex>
          {typeTag && (
            <Flex alignSelf="flex-start" h="26px" px="10px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" fontSize="11px" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" lineHeight="normal">
              {typeTag}
            </Flex>
          )}
          <Text as="h1" m={0} fontSize="22px" lineHeight={1.25} fontWeight={600}>
            {campaign.name}
          </Text>
          {expiresAt && (
            <Flex alignSelf="flex-start" h="28px" px="10px" borderRadius="full" bg="#FBE1DC" color="#A1260F" align="center" gap="6px" fontSize="12px" fontWeight={600} lineHeight="normal">
              <Icon as={FiClock} boxSize="14px" strokeWidth={2.4} aria-hidden />
              {formatExpiresIn(expiresAt)}
            </Flex>
          )}
        </VStack>

        {banners.map(banner => (
          <Flex
            key={banner.title}
            role="status"
            p="14px"
            borderRadius="16px"
            bg={banner.ok ? '#E4F3CC' : '#FFF1D6'}
            color={banner.ok ? '#2C5208' : '#5C3900'}
            gap="10px"
          >
            <Icon as={banner.ok ? FiCheck : FiAlertTriangle} boxSize="20px" flex="none" mt="1px" strokeWidth={banner.ok ? 2.6 : 2.2} aria-hidden />
            <VStack as="span" align="stretch" spacing="3px">
              <Text as="strong" fontSize="15px" lineHeight="normal">{banner.title}</Text>
              <Text as="span" fontSize="14px" lineHeight={1.4}>{banner.sub}</Text>
            </VStack>
          </Flex>
        ))}

        <Box position="relative">
          <VStack
            align="stretch"
            spacing="24px"
            aria-hidden={needsVerification || undefined}
            sx={needsVerification ? { filter: 'blur(9px)', opacity: 0.6, pointerEvents: 'none', userSelect: 'none' } : undefined}
          >
            {/* Brand's counter note */}
            {!needsVerification && collab.clientCounterNote && (
              <VStack align="stretch" spacing="10px" p="14px" borderRadius="18px" bg={ff.grey}>
                <Flex align="center" gap="10px">
                  <Avatar
                    src={client?.instagramProfilePicture}
                    name={client?.accountName}
                    boxSize="32px"
                    borderRadius="10px"
                    bg={ff.card}
                    color={ff.ink}
                    flex="none"
                    sx={{ '& > [role=img]': { fontSize: '12px', fontWeight: 600 }, img: { borderRadius: '10px' } }}
                  />
                  <Text flex={1} fontSize="13px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.body} lineHeight="normal">
                    Note from {brandName}
                  </Text>
                  {brandCountered && (
                    <Flex h="28px" px="10px" borderRadius="full" bg="#E4F3CC" color="#2C5208" align="center" fontSize="12px" fontWeight={600} lineHeight="normal" flex="none">
                      New counter
                    </Flex>
                  )}
                </Flex>
                <Text fontSize="15px" lineHeight={1.45} whiteSpace="pre-wrap">{collab.clientCounterNote}</Text>
                {yourCounterLine && <Text fontSize="13px" color="#55555A" lineHeight="normal">{yourCounterLine}</Text>}
              </VStack>
            )}

            {/* You get (hidden until the phone is verified) */}
            {!needsVerification && (
              <VStack bg={ff.limeSurface} borderRadius="22px" p="18px" spacing="16px" align="stretch">
                <VStack align="stretch" spacing="10px">
                  <Text fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2} lineHeight="normal">
                    {brandCountered ? `New terms from ${brandName}` : 'You get'}
                  </Text>
                  <Flex align="flex-end" gap="14px" wrap="wrap">
                    {money.map((m, i) => (
                      <Flex as="span" key={m.label} align="flex-end" gap="14px">
                        {i > 0 && (
                          <Text as="span" fontSize="26px" lineHeight={1} fontWeight={300} color="#8A8A8E" aria-hidden>+</Text>
                        )}
                        <Flex as="span" align="baseline" gap="6px">
                          <Text as="span" textStyle="display" fontSize="29px" letterSpacing="0" textTransform="none">${m.value}</Text>
                          <Text as="span" fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" lineHeight="normal">{m.label}</Text>
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                </VStack>
                <Box h="1px" bg="#E0E0DD" />
                <VStack as="ul" listStyleType="none" align="stretch" spacing="10px" m={0} p={0}>
                  {deliverablesLine && (
                    <Fact icon={FiVideo}>
                      <b>{deliverablesLine}</b>
                      {postingNote ? ` · ${postingNote}` : ''}
                    </Fact>
                  )}
                  {productValue > 0 && (
                    <Fact icon={FiPackage}>
                      <b>{productLabel || 'Free product'}</b> · ${productValue} value
                    </Fact>
                  )}
                  {brandHandlesCredit && (
                    <Fact icon={FiCheck}>
                      <b>Credit handled by {brandName}</b>
                    </Fact>
                  )}
                  {turnaround && (
                    <Fact icon={FiClock}>
                      <b>{getTurnaroundTitle(campaign, collab)}:</b> {turnaround}
                    </Fact>
                  )}
                </VStack>
              </VStack>
            )}

            <CreativeBrief collab={collab} campaign={campaign} hideBrandHeader />
          </VStack>

          {needsVerification && (
            <VStack
              position="absolute"
              top="40px"
              left={0}
              right={0}
              px="20px"
              py="24px"
              borderRadius="24px"
              bg="white"
              boxShadow="0 12px 40px rgba(14,14,14,0.18)"
              spacing="12px"
              textAlign="center"
            >
              <Flex w="56px" h="56px" borderRadius="full" bg={ff.ink} color={ff.lime} align="center" justify="center">
                <Icon as={FiSmartphone} boxSize="24px" strokeWidth={2.2} aria-hidden />
              </Flex>
              <Text as="h2" m={0} textStyle="display" fontSize="27px" lineHeight={1.12} letterSpacing="0">
                Verify your phone to view the full offer
              </Text>
              <Text maxW="280px" fontSize="14px" lineHeight={1.4} color="#55555A">
                We text you about visits and timing. Add your number to see the pay, brief and locations.
              </Text>
              <Button mt="4px" h="48px" px="22px" borderRadius="full" bg={ff.lime} color={ff.ink} fontSize="15px" fontWeight={600} lineHeight="normal" _hover={{ bg: ff.lime }} onClick={() => setIsPhoneModalOpen(true)}>
                Add phone number
              </Button>
            </VStack>
          )}
        </Box>
      </VStack>

      {/* Floating action dock (no tab bar on this route) */}
      <Box position="fixed" left="12px" right="12px" bottom="calc(20px + env(safe-area-inset-bottom))" zIndex={20}>
        <VStack maxW="560px" mx="auto" p="12px" borderRadius="26px" bg={ff.ink} color="white" spacing="10px" align="stretch">
          {needsVerification ? (
            <Button {...PRIMARY} onClick={() => setIsPhoneModalOpen(true)}>
              Verify phone to see offer
            </Button>
          ) : (
            <>
              {(postBy.required || postBy.blockedReason) && (
                <Box color={ff.ink}>
                  <PostByConfirm ref={postByCheckboxRef} state={postBy} />
                </Box>
              )}
              {campaignFull ? (
                <Button
                  {...PRIMARY}
                  onClick={() => setIsExtensionRequestModalOpen(true)}
                  isDisabled={waitlistRequested}
                >
                  {waitlistRequested ? 'Waitlist requested' : 'Collab full · Join waitlist'}
                </Button>
              ) : showRequestExtension ? (
                <Button
                  {...PRIMARY}
                  onClick={() => setIsExtensionRequestModalOpen(true)}
                  isDisabled={extensionRequested}
                  isLoading={isSchedulingLoading}
                >
                  {extensionRequested ? 'Extension Requested' : 'Request Extension'}
                </Button>
              ) : (
                <Button
                  {...PRIMARY}
                  onClick={() => handleAccept()}
                  isDisabled={acceptDisabled}
                  isLoading={isAccepting || isSpotsLoading || isSchedulingLoading}
                >
                  {acceptLabelFor(campaign)}
                </Button>
              )}
              <Flex align="center" gap="8px">
                {counterAllowed ? (
                  <Button {...DOCK_BUTTON} onClick={handleCounter}>
                    Counter
                  </Button>
                ) : (
                  <Flex
                    flex={1}
                    minW={0}
                    minH="44px"
                    px="10px"
                    borderRadius="14px"
                    border="1px dashed #55555A"
                    direction="column"
                    justify="center"
                    fontSize="12px"
                    lineHeight={1.25}
                    color="#CFCFD2"
                  >
                    {campaign.allowCounters ? (
                      <>
                        <Text as="b" color="white">Counter limit reached</Text>
                        {Math.min(countersUsed, MAX_COUNTERS)} of {MAX_COUNTERS} used
                      </>
                    ) : (
                      <>
                        <Text as="b" color="white">Counters are off</Text>
                        for this invite
                      </>
                    )}
                  </Flex>
                )}
                <Button {...DOCK_BUTTON} onClick={handleReject}>
                  Decline
                </Button>
                <IconButton
                  aria-label={`Message ${brandName}`}
                  icon={<FiMessageSquare size={20} strokeWidth={2.2} />}
                  onClick={handleMessageBrand}
                  {...DOCK_BUTTON}
                  flex="none"
                  w="44px"
                  minW="44px"
                />
              </Flex>
            </>
          )}
        </VStack>
      </Box>

      {/* Modals */}
      <ShippingDetailsModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        collab={collab}
        campaign={campaign}
        goLive={postBy}
      />
      <CounterReasonModal
        isOpen={isCounterReasonModalOpen}
        onClose={() => setIsCounterReasonModalOpen(false)}
        onSubmit={handleModalSubmit}
        onBack={() => setIsCounterReasonModalOpen(true)}
        collab={collab}
        campaign={campaign}
      />
      <DeclineReasonModal
        isOpen={isDeclineReasonModalOpen}
        onClose={() => setIsDeclineReasonModalOpen(false)}
        onSubmit={handleModalSubmit}
        onBack={() => setIsDeclineReasonModalOpen(true)}
        collab={collab}
        campaign={campaign}
      />
      <PhoneVerificationModal
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
      />
      <ExtensionRequestModal
        isOpen={isExtensionRequestModalOpen}
        onClose={() => setIsExtensionRequestModalOpen(false)}
        clientName={client?.accountName || 'the brand'}
        clientEmail={client?.email || ''}
        clientId={client?.id || ''}
        influencerHandle={getInfluencerPrimaryHandle(influencerData)}
        influencerPhone={influencerData?.phoneNumber || ''}
        collabId={collab?.id || ''}
        collabStatus={collab?.status || ''}
        isCampaignFull={campaign && collab ? isCampaignFull(campaign, remainingSpots, collab) : false}
        endDatePassed={campaign && collab ? isCampaignEnded(campaign, collab) : false}
        schedulingFullyBooked={schedulingFullyBooked && !!(campaign && collab && !isCampaignEnded(campaign, collab))}
        postByNoSlotsMessage={postByNoSlotsMessage}
        collabEndDate={campaign && collab ? formatFirebaseDate(getEffectiveEndDate(campaign, collab)) : ''}
        collab={collab}
      />
    </Box>
  );
};

export default ReviewCollabPage;
